import { icon, type IconName } from './icons';
import { t } from './i18n/i18n';
import { formatCeil, sumCeil } from './format-short';
import type { AttackResultPayload, DefenceResultPayload, GameNotification } from '../screens/notifications/notifications-api';

/**
 * The "battle is over" card shown on the world map and the colony view: a compact summary (verdict, where, what was
 * pillaged or lost, when). Tapping it opens the Battle log; otherwise it leaves on its own after 10 seconds. Other
 * screens keep using the ordinary toast.
 */

export const BATTLE_ALERT_MS = 10_000;
const MAX_STACK = 3;
const ALERT_TABS = ['worldmap', 'colony'];

type Tone = 'win' | 'loss' | 'scout';

export interface BattleAlertModel {
  tone: Tone;
  iconName: IconName;
  title: string;
  destination: string;
  /** Pillaged loot, units lost, etc. — each a (icon, text) chip. */
  chips: { icon: IconName; text: string }[];
  time: string;
}

/** The alert only appears over the map and the colony; true while one of them is on screen. */
export function alertTabActive(): boolean {
  return ALERT_TABS.includes(document.documentElement.dataset.activeTab ?? '');
}

export function isBattleResult(n: GameNotification): boolean {
  return n.kind === 'ATTACK_RESULT' || n.kind === 'DEFENCE_RESULT';
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Pure: the card's content for one finished battle. Attack = you marched; defence = you were marched on. */
export function battleAlertModel(n: GameNotification): BattleAlertModel | null {
  const time = timeOf(n.createdAt);
  if (n.kind === 'ATTACK_RESULT') {
    const p = n.payload as AttackResultPayload;
    if (p.isRecon) {
      return {
        tone: p.attackerWins ? 'scout' : 'loss',
        iconName: 'map',
        title: t(p.attackerWins ? 'battle.alert.scouted' : 'battle.alert.scoutFailed'),
        destination: p.defenderColonyName,
        chips: p.unitsLost > 0 ? [{ icon: 'skull', text: String(p.unitsLost) }] : [],
        time,
      };
    }
    const loot = sumCeil(p.plunder);
    const chips: BattleAlertModel['chips'] = [];
    if (p.attackerWins && loot > 0) chips.push({ icon: 'crate', text: t('battle.alert.pillaged', { amount: formatCeil(loot) }) });
    if (p.captured) chips.push({ icon: 'check', text: t('battle.alert.captured') });
    if (p.unitsLost > 0) chips.push({ icon: 'skull', text: String(p.unitsLost) });
    return {
      tone: p.attackerWins ? 'win' : 'loss',
      iconName: p.attackerWins ? 'crosshair' : 'skull',
      title: t(p.attackerWins ? 'battle.alert.won' : 'battle.alert.lost'),
      destination: p.defenderColonyName,
      chips,
      time,
    };
  }
  if (n.kind === 'DEFENCE_RESULT') {
    const p = n.payload as DefenceResultPayload;
    if (p.isRecon) {
      return { tone: 'scout', iconName: 'map', title: t('battle.alert.scoutedYou'), destination: p.defenderColonyName, chips: [], time };
    }
    const taken = sumCeil(p.plunderTaken);
    const chips: BattleAlertModel['chips'] = [];
    if (p.attackerWins && taken > 0) chips.push({ icon: 'crate', text: t('battle.alert.plundered', { amount: formatCeil(taken) }) });
    if (p.colonyLost) chips.push({ icon: 'skull', text: t('battle.alert.colonyLost') });
    if (p.unitsLost > 0) chips.push({ icon: 'skull', text: String(p.unitsLost) });
    return {
      tone: p.attackerWins ? 'loss' : 'win',
      iconName: p.attackerWins ? 'skull' : 'shield',
      title: t(p.attackerWins ? 'battle.alert.raided' : 'battle.alert.heldOff', { attacker: p.attackerColonyName }),
      destination: p.defenderColonyName,
      chips,
      time,
    };
  }
  return null;
}

function escapeHtml(value: string): string {
  const d = document.createElement('div');
  d.textContent = value;
  return d.innerHTML;
}

let host: HTMLElement | null = null;

function getHost(): HTMLElement {
  if (host && host.isConnected) return host;
  host = document.createElement('div');
  host.id = 'battle-alerts';
  host.setAttribute('aria-live', 'polite');
  document.body.appendChild(host);
  return host;
}

/** Shows the card for a finished battle. Returns false (and shows nothing) when the notification is not a battle result. */
export function showBattleAlert(n: GameNotification): boolean {
  const m = battleAlertModel(n);
  if (!m) return false;
  const container = getHost();
  while (container.children.length >= MAX_STACK) container.firstElementChild?.remove();

  const el = document.createElement('button');
  el.type = 'button';
  el.className = `battle-alert battle-alert--${m.tone}`;
  el.setAttribute('data-battle-alert', m.tone);
  el.setAttribute('aria-label', `${m.title} — ${m.destination}. ${t('battle.alert.open')}`);
  el.innerHTML = `
    <span class="battle-alert-ico" aria-hidden="true">${icon(m.iconName)}</span>
    <span class="battle-alert-body">
      <span class="battle-alert-title">${escapeHtml(m.title)}</span>
      <span class="battle-alert-dest"><span class="battle-alert-arrow" aria-hidden="true">${icon('crosshair')}</span>${escapeHtml(m.destination)}</span>
      <span class="battle-alert-chips">
        ${m.chips.map((c) => `<span class="battle-alert-chip"><span aria-hidden="true">${icon(c.icon)}</span>${escapeHtml(c.text)}</span>`).join('')}
        ${m.time ? `<span class="battle-alert-chip battle-alert-time"><span aria-hidden="true">${icon('clock')}</span>${escapeHtml(m.time)}</span>` : ''}
      </span>
    </span>
    <span class="battle-alert-bar" aria-hidden="true"></span>`;
  el.style.setProperty('--alert-ms', `${BATTLE_ALERT_MS}ms`);

  let timer: number | null = null;
  function dismiss(): void {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    el.classList.add('battle-alert--out');
    window.setTimeout(() => el.remove(), 250);
  }
  el.addEventListener('click', () => {
    dismiss();
    // The shell switches tabs on this event (same channel the offer banner uses).
    window.dispatchEvent(new CustomEvent('shell:open-tab', { detail: 'battle' }));
  });
  timer = window.setTimeout(dismiss, BATTLE_ALERT_MS);
  container.appendChild(el);
  return true;
}
